import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import { teachers } from '@/mocks/teachers';
import { mockParents } from '@/mocks/announcements';

interface CreateAnnouncementModalProps {
  open: boolean;
  onClose: () => void;
  onPublish: (data: {
    message: string;
    audioBlob?: Blob;
    facultyIds: string[] | 'all';
    parentIds: string[] | 'all';
    scheduleAt?: string;
  }) => void;
}

export default function CreateAnnouncementModal({ open, onClose, onPublish }: CreateAnnouncementModalProps) {
  const { t } = useTranslation();

  // Message
  const [message, setMessage] = useState('');

  // Audio recording
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Faculty selection
  const [facultyMode, setFacultyMode] = useState<'all' | 'custom' | null>(null);
  const [selectedFaculty, setSelectedFaculty] = useState<string[]>([]);
  const [facultySearch, setFacultySearch] = useState('');

  // Parents selection
  const [parentMode, setParentMode] = useState<'all' | 'custom' | null>(null);
  const [selectedParents, setSelectedParents] = useState<string[]>([]);
  const [parentSearch, setParentSearch] = useState('');

  // Schedule
  const [scheduleOption, setScheduleOption] = useState<'now' | 'later'>('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  // Publish state
  const [publishing, setPublishing] = useState(false);
  const [errors, setErrors] = useState<{ message?: string; recipients?: string }>({});

  // ── Audio recording ──
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        if (audioURL) URL.revokeObjectURL(audioURL);
        setAudioURL(url);
        stream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch {
      // Fallback for environments without mic access
      setIsRecording(true);
    }
  }, [audioURL]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  // ── Faculty ──
  const handleFacultySelectAll = () => setFacultyMode('all');
  const handleFacultyUnselectAll = () => { setFacultyMode(null); setSelectedFaculty([]); };
  const handleFacultyCustom = () => setFacultyMode('custom');

  const toggleFaculty = (id: string) => {
    setSelectedFaculty((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );
    setFacultyMode('custom');
  };

  const activeTeachers = teachers.filter((t) => t.status === 'active');
  const filteredFaculty = facultySearch
    ? activeTeachers.filter((t) => t.name.toLowerCase().includes(facultySearch.toLowerCase()))
    : activeTeachers;

  // ── Parents ──
  const handleParentSelectAll = () => setParentMode('all');
  const handleParentUnselectAll = () => { setParentMode(null); setSelectedParents([]); };
  const handleParentCustom = () => setParentMode('custom');

  const toggleParent = (id: string) => {
    setSelectedParents((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
    setParentMode('custom');
  };

  const filteredParents = parentSearch
    ? mockParents.filter((p) => p.name.toLowerCase().includes(parentSearch.toLowerCase()))
    : mockParents;

  // ── Validation ──
  const validate = (): boolean => {
    const newErrors: { message?: string; recipients?: string } = {};
    if (!message.trim()) newErrors.message = t('dashboard.messageRequired');
    if (!facultyMode && selectedFaculty.length === 0 && !parentMode && selectedParents.length === 0) {
      newErrors.recipients = t('dashboard.recipientsRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Publish ──
  const handlePublish = async () => {
    if (!validate()) return;
    setPublishing(true);

    // Simulate publish delay
    await new Promise((r) => setTimeout(r, 800));

    onPublish({
      message: message.trim(),
      audioBlob: audioBlob ?? undefined,
      facultyIds: facultyMode === 'all' ? 'all' : selectedFaculty,
      parentIds: parentMode === 'all' ? 'all' : selectedParents,
      scheduleAt: scheduleOption === 'later' && scheduleDate ? `${scheduleDate}T${scheduleTime || '09:00'}:00` : undefined,
    });

    // Reset form
    setMessage('');
    setAudioBlob(null);
    setAudioURL(null);
    setIsRecording(false);
    setFacultyMode(null);
    setSelectedFaculty([]);
    setFacultySearch('');
    setParentMode(null);
    setSelectedParents([]);
    setParentSearch('');
    setScheduleOption('now');
    setScheduleDate('');
    setScheduleTime('');
    setErrors({});
    setPublishing(false);
  };

  const handleClose = () => {
    if (publishing) return;
    setMessage('');
    setAudioBlob(null);
    if (audioURL) URL.revokeObjectURL(audioURL);
    setAudioURL(null);
    setIsRecording(false);
    setFacultyMode(null);
    setSelectedFaculty([]);
    setFacultySearch('');
    setParentMode(null);
    setSelectedParents([]);
    setParentSearch('');
    setScheduleOption('now');
    setScheduleDate('');
    setScheduleTime('');
    setErrors({});
    onClose();
  };

  // ── Helpers ──
  const getRecipientsSummary = (): string => {
    const parts: string[] = [];
    if (facultyMode === 'all') parts.push(t('dashboard.allFaculty'));
    else if (facultyMode === 'custom' && selectedFaculty.length > 0) parts.push(t('dashboard.selectedFaculty', { count: selectedFaculty.length }));

    if (parentMode === 'all') parts.push(t('dashboard.allParents'));
    else if (parentMode === 'custom' && selectedParents.length > 0) parts.push(t('dashboard.selectedParents', { count: selectedParents.length }));

    if (parts.length === 0) return t('dashboard.noRecipients');
    return parts.join(' & ');
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('dashboard.newAnnouncement')}
      subtitle={t('dashboard.announcementsSubtitle')}
      size="lg"
      footer={
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleClose} disabled={publishing}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handlePublish}
            loading={publishing}
            iconLeft="ri-send-plane-line"
          >
            {scheduleOption === 'later' ? t('dashboard.scheduleLater') : t('dashboard.publishAnnouncement')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {/* ── Schedule Toggle ── */}
        <div>
          <label className="block text-sm font-medium text-foreground-700 font-label mb-2">
            {t('dashboard.scheduleFor')}
          </label>
          <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
            <button
              onClick={() => setScheduleOption('now')}
              className={`flex-1 px-4 py-2 text-sm font-label font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                scheduleOption === 'now'
                  ? 'bg-background-50 text-foreground-900 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className="ri-flashlight-line mr-1.5" />
              {t('dashboard.publishNow')}
            </button>
            <button
              onClick={() => setScheduleOption('later')}
              className={`flex-1 px-4 py-2 text-sm font-label font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                scheduleOption === 'later'
                  ? 'bg-background-50 text-foreground-900 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className="ri-time-line mr-1.5" />
              {t('dashboard.scheduleLater')}
            </button>
          </div>

          {scheduleOption === 'later' && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground-600 font-label">
                  {t('dashboard.scheduleDateTime')}
                </label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="xl-input text-sm"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground-600 font-label">&nbsp;</label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="xl-input text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Faculty Selection ── */}
        <div>
          <label className="block text-sm font-medium text-foreground-700 font-label mb-2">
            {t('dashboard.selectFaculty')}
          </label>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <button
              onClick={handleFacultySelectAll}
              className={`px-3 py-1.5 text-xs font-label font-medium rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                facultyMode === 'all'
                  ? 'bg-primary-500 text-background-50 border-primary-500'
                  : 'bg-background-50 text-foreground-600 border-background-300 hover:bg-background-100'
              }`}
            >
              <i className="ri-check-double-line mr-1" />
              {t('dashboard.selectAll')}
            </button>
            <button
              onClick={handleFacultyCustom}
              className={`px-3 py-1.5 text-xs font-label font-medium rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                facultyMode === 'custom'
                  ? 'bg-secondary-500 text-background-50 border-secondary-500'
                  : 'bg-background-50 text-foreground-600 border-background-300 hover:bg-background-100'
              }`}
            >
              {t('dashboard.selectedCount', { count: selectedFaculty.length })}
            </button>
            {(facultyMode || selectedFaculty.length > 0) && (
              <button
                onClick={handleFacultyUnselectAll}
                className="px-3 py-1.5 text-xs font-label font-medium rounded-full border border-background-300 bg-background-50 text-foreground-500 hover:text-semantic-error hover:border-semantic-error/30 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line mr-1" />
                {t('dashboard.unselectAll')}
              </button>
            )}
          </div>

          {facultyMode === 'custom' && (
            <div className="border border-background-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-background-200 bg-background-100">
                <input
                  type="text"
                  placeholder="Search faculty..."
                  value={facultySearch}
                  onChange={(e) => setFacultySearch(e.target.value)}
                  className="w-full bg-transparent text-xs text-foreground-700 placeholder:text-foreground-400 outline-none font-label"
                />
              </div>
              <div className="max-h-40 overflow-y-auto divide-y divide-background-100">
                {filteredFaculty.map((fac) => (
                  <label
                    key={fac.id}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-background-100 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedFaculty.includes(fac.id)}
                      onChange={() => toggleFaculty(fac.id)}
                      className="w-4 h-4 rounded accent-primary-500 cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-label font-medium text-foreground-800 truncate">{fac.name}</p>
                      <p className="text-xs text-foreground-500 font-label truncate">{fac.role} · {fac.className}</p>
                    </div>
                  </label>
                ))}
                {filteredFaculty.length === 0 && (
                  <p className="px-3 py-4 text-xs text-foreground-500 font-label text-center">
                    {t('dashboard.noFacultySelected')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Parents Selection ── */}
        <div>
          <label className="block text-sm font-medium text-foreground-700 font-label mb-2">
            {t('dashboard.selectParents')}
          </label>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <button
              onClick={handleParentSelectAll}
              className={`px-3 py-1.5 text-xs font-label font-medium rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                parentMode === 'all'
                  ? 'bg-primary-500 text-background-50 border-primary-500'
                  : 'bg-background-50 text-foreground-600 border-background-300 hover:bg-background-100'
              }`}
            >
              <i className="ri-check-double-line mr-1" />
              {t('dashboard.selectAll')}
            </button>
            <button
              onClick={handleParentCustom}
              className={`px-3 py-1.5 text-xs font-label font-medium rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                parentMode === 'custom'
                  ? 'bg-secondary-500 text-background-50 border-secondary-500'
                  : 'bg-background-50 text-foreground-600 border-background-300 hover:bg-background-100'
              }`}
            >
              {t('dashboard.selectedCount', { count: selectedParents.length })}
            </button>
            {(parentMode || selectedParents.length > 0) && (
              <button
                onClick={handleParentUnselectAll}
                className="px-3 py-1.5 text-xs font-label font-medium rounded-full border border-background-300 bg-background-50 text-foreground-500 hover:text-semantic-error hover:border-semantic-error/30 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line mr-1" />
                {t('dashboard.unselectAll')}
              </button>
            )}
          </div>

          {parentMode === 'custom' && (
            <div className="border border-background-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 border-b border-background-200 bg-background-100">
                <input
                  type="text"
                  placeholder="Search parents..."
                  value={parentSearch}
                  onChange={(e) => setParentSearch(e.target.value)}
                  className="w-full bg-transparent text-xs text-foreground-700 placeholder:text-foreground-400 outline-none font-label"
                />
              </div>
              <div className="max-h-40 overflow-y-auto divide-y divide-background-100">
                {filteredParents.map((par) => (
                  <label
                    key={par.id}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-background-100 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedParents.includes(par.id)}
                      onChange={() => toggleParent(par.id)}
                      className="w-4 h-4 rounded accent-primary-500 cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-label font-medium text-foreground-800 truncate">{par.name}</p>
                      <p className="text-xs text-foreground-500 font-label truncate">{par.studentName}</p>
                    </div>
                  </label>
                ))}
                {filteredParents.length === 0 && (
                  <p className="px-3 py-4 text-xs text-foreground-500 font-label text-center">
                    {t('dashboard.noParentsSelected')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Recipients Summary ── */}
        <div className="flex items-center gap-2 px-3 py-2 bg-background-100 rounded-lg">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
            <i className="ri-group-line text-primary-600 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-foreground-500 font-label">{t('dashboard.selectedRecipients')}</p>
            <p className="text-sm font-label font-medium text-foreground-800 truncate">{getRecipientsSummary()}</p>
          </div>
        </div>

        {/* ── Message ── */}
        <div>
          <label className="block text-sm font-medium text-foreground-700 font-label mb-2">
            {t('dashboard.announcementMessage')}
          </label>
          <textarea
            value={message}
            onChange={(e) => { setMessage(e.target.value); if (errors.message) setErrors((prev) => ({ ...prev, message: undefined })); }}
            placeholder={t('dashboard.messagePlaceholder')}
            rows={4}
            maxLength={2000}
            className={`xl-input w-full resize-none text-sm ${errors.message ? 'error' : ''}`}
          />
          <div className="flex items-center justify-between mt-1.5">
            <p className="text-xs text-foreground-400 font-label">{message.length}/2000</p>
            {errors.message && (
              <p className="text-xs text-semantic-error font-label flex items-center gap-1">
                <i className="ri-error-warning-line" />{errors.message}
              </p>
            )}
          </div>
        </div>

        {/* ── Audio Recording ── */}
        <div>
          <label className="block text-sm font-medium text-foreground-700 font-label mb-2">
            {t('dashboard.recordAudio')} <span className="text-foreground-400 font-normal">({t('common.optional')})</span>
          </label>

          {!audioURL ? (
            <div className="flex items-center gap-3">
              {isRecording ? (
                <button
                  onClick={stopRecording}
                  className="flex items-center gap-2 px-4 py-2.5 bg-semantic-error text-background-50 text-sm font-label font-medium rounded-lg hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-background-50 animate-pulse" />
                  <i className="ri-stop-fill" />
                  {t('dashboard.stopRecording')}
                </button>
              ) : (
                <button
                  onClick={startRecording}
                  className="flex items-center gap-2 px-4 py-2.5 bg-background-100 border border-background-300 text-foreground-700 text-sm font-label font-medium rounded-lg hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mic-line text-lg" />
                  {t('dashboard.recordAudio')}
                </button>
              )}
              {isRecording && (
                <span className="text-xs text-semantic-error font-label font-medium animate-pulse">
                  {t('dashboard.recordingAudio')}
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-green-100 flex-shrink-0">
                <i className="ri-mic-fill text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-label font-medium text-green-800">{t('dashboard.audioMessageAttached')}</p>
                <audio controls src={audioURL} className="mt-1.5 w-full h-8" />
              </div>
              <button
                onClick={() => {
                  if (audioURL) URL.revokeObjectURL(audioURL);
                  setAudioURL(null);
                  setAudioBlob(null);
                }}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:text-semantic-error hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-delete-bin-line" />
              </button>
            </div>
          )}
        </div>

        {/* ── Global recipients error ── */}
        {errors.recipients && (
          <p className="text-sm text-semantic-error font-label flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <i className="ri-error-warning-line" />
            {errors.recipients}
          </p>
        )}
      </div>
    </Modal>
  );
}